import CoreIcon from '@components/std-components/CoreIcon'
import { SmallRecommendedIcon } from '@components/std-components/RecommendedIcon'
import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell/Cell'
import {
  useEnhancedProductStatus,
  useProductStatusForWarning
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import ProductCardSkeleton from '@components/UI-Components/ProductCardSkeleton'
import { ProductCardDirection } from '@components/UI-Components/ProductCardSkeleton/ProductCardSkeleton'
import MdRemove from '@meronex/icons/md/MdRemove'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { PRODUCT_REMOVE_LOADER_KEY } from '@modules/order/constants/uiConstants'
import OrderContext from '@modules/order/contexts/order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { stores } from '@stores'
import { getEnumValue } from '@utils/get-enum-value'
import { useObserver } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import React, { FC, useCallback, useContext } from 'react'
import { ProductCardSizes } from '@constants'
import CNFLandscapeProductCard from '@components/UI-Components/LandscapeProductCard/CNFLandscapeProductCard'
import { prepareProductCardFieldsData } from '@modules/order/utils/productCardFieldsConfig'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { makeStyles } from '@material-ui/core'
import { getValidBSGStatuses } from '@modules/showroom/productCollection/hooks/getValidBSGStatuses'
import { getEnhancedImageURL } from '@modules/showroom/productCollection/hooks/getEnhancedImageURL'
import { config } from '@config'
import { View } from '@modules/order/constants/views'
import classNames from 'classnames'

const useStyles = makeStyles(theme => ({
  removeBtn: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  removeIcon: {
    display: 'inline-block',
    height: '100%',
    width: '100%'
  },
  cnfProductInfoContainer: {
    padding: '15px 0'
  },
  topCornerTriangle: {
    position: 'relative',
    '&:after': {
      content: '""',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '0 20px 20px 0',
      borderColor: 'transparent #d90000 transparent transparent',
      right: 0,
      top: 0,
      position: 'absolute'
    }
  }
}))
const CNFProductInfo: FC<CellItem> = ({ rowData }) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const viewContextVal = useViewContext()
    const uiStore = viewContextVal.uiStore as EnhancedListUIStore<any>
    const {
      uiStore: { isOrderEditable }
    } = useContext(OrderContext)
    const classes = useStyles()
    const {
      nav: {
        queryParams: { clientType, buyingSessionGroupId, view }
      },
      productConfiguration: { allAttributesFlattenObject, initialAllocationActivated }
    } = stores
    const isProductActive: boolean = useEnhancedProductStatus(rowData)
    const hasWarning = useProductStatusForWarning(
      uiStore.dataStore.enhancedWarningPopupStore?.mandatoryWarningAttributes,
      rowData
    )
    const productIndex = uiStore.dataStore.itemIndexObjectStore.getIndexByKey(rowData?.id)

    const onBottomIconClickHandler = useCallback(() => {
      uiStore.removeItemObjectFromHierarchy(
        productIndex === -1 ? 0 : productIndex,
        uiStore.getFocusedProductHierarchy(productIndex === -1 ? 0 : productIndex),
        true
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productIndex])

    const deletingInProgress =
      uiStore.dataStore.itemIndexObjectStore.isObjectsDataLoading(
        productIndex,
        productIndex + 1,
        PRODUCT_REMOVE_LOADER_KEY
      )

    const validBSGStatuses = getValidBSGStatuses(buyingSessionGroupId, clientType)
    let imageUrl = getEnhancedImageURL(rowData)

    const channelWisefields = prepareProductCardFieldsData(
      uiStore?.currentViewConfig,
      rowData,
      allAttributesFlattenObject,
      t
    )
    if (!rowData) {
      // Loader
      return (
        <ProductCardSkeleton
          size={ProductCardSizes.Small}
          direction={ProductCardDirection.Horizontal}
        />
      )
    }

    return (
      <div
        className={classNames(classes.cnfProductInfoContainer, {
          [classes.topCornerTriangle]: hasWarning
        })}
      >
        <CNFLandscapeProductCard
          fields={channelWisefields}
          isActive={isProductActive}
          onClick={e => {
            uiStore.onProductDetailClick(rowData.id)(e)
          }}
          bottomRightIcon={
            deletingInProgress ||
            !validBSGStatuses ||
            (clientType === getEnumValue(VIPClientType.Internal) && rowData?.isCore) ||
            (config.allowAddremoveProductInOrderView === 'false' &&
              view !== View.AddProduct) ||
            initialAllocationActivated
              ? null
              : isOrderEditable && (
                  <div className={classes.removeBtn}>
                    <MdRemove
                      onClick={onBottomIconClickHandler}
                      className={classes.removeIcon}
                      size={22}
                    />
                  </div>
                )
          }
          size={ProductCardSizes.Landscape}
          imageUrl={imageUrl}
          showCrossLine={!isProductActive}
          disabled={deletingInProgress || !validBSGStatuses}
          topRightIcon={
            clientType === getEnumValue(VIPClientType.External) ? (
              rowData.isRecommended ? (
                <SmallRecommendedIcon />
              ) : null
            ) : rowData.isCore ? (
              <CoreIcon />
            ) : null
          }
        />
      </div>
    )
  })

export default CNFProductInfo
